import styles from "./PlannerEvent.module.scss";
import { classNames } from "primereact/utils";
import { DefaultSection } from "../../../common/components/common/DefaultSection";
import { ItemsList } from "../../../common/components/itemsList/ItemsList";
import { IPlannerEventUserModel } from "./models/plannerEventUserModel";
import {
  constructPlannerEventDuplicationHref,
  constructPlannerEventSignUpHref,
} from "../../../common/infrastructure/routes/routePaths";
import { IconType } from "../../../common/icons/Icon";
import { SpecificPageNotFound } from "../../../common/components/errorPages/SpecificPageNotFound";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../common/components/skeletons/SkeletonItem";
import { IconButton } from "../../../common/components/button/iconButton/IconButton";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { SectionInfoRow } from "../../../common/components/common/SectionInfoRow";
import { EventType, UserEventStatus } from "../../../common/enums";
import {
  EventTypeShortLabels,
  UserEventStatusLabels,
} from "../../../common/labels";
import { EXCLUDED_RESERVATION_STATUSES } from "../../../common/infrastructure/utilities/reservationUtility";
import { AppTooltip } from "../../../common/components/tooltip/AppTooltip";
import { PlannerEventUserItem } from "./components/PlannerEventUserItem";
import { PlannerEventMeeting } from "./components/PlannerEventMeeting";
import { usePlannerEvent } from "./hooks/usePlannerEvent";
import { UNDO_ARRIVAL_PROMPT } from "./hooks/usePlannerEventReservationDetailsReservation";
import { AppDialog } from "../../../common/components/AppDialog/AppDialog";
import { useStaysMountedToClose } from "../../../common/components/AppDialog/hooks/useStaysMountedToClose";
import { PlannerEventReservationDetails } from "./components/PlannerEventReservationDetails";
import { NotesButton } from "../../../common/components/common/NotesDialog/NotesButton";
import { PriceDisplay } from "../../../common/components/PriceDisplay/PriceDisplay";
import { PlannerEventReservationDetailsPaymentMethodDialog } from "./components/PlannerEventReservationDetailsPaymentMethodDialog";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { MusicSuggestionsCountBadge } from "./components/MusicSuggestionsCountBadge";
import { MusicSuggestionsDialog } from "../../portal/musicSuggestions/MusicSuggestionsDialog";
import { HistoryButton } from "../../../common/components/history/HistoryButton";
import { EVENT_HISTORY_CONFIG } from "../../../common/utilities/eventAuditLogUtility";
import { WorkoutPlanButton } from "../../../common/components/workoutPlanDialog/WorkoutPlanButton";
import { MultisportTag } from "../../../common/components/common/MultisportTag/MultisportTag";
import { useMeetingLinkRecipientsWarning } from "../common/hooks/useMeetingLinkRecipientsWarning";
import {
  canQuickArrive,
  isAwaitingArrivalPayment,
} from "./utils/quickArrivalUtility";
import { useRemoveEventParticipant } from "./hooks/useRemoveEventParticipant";

export const PlannerEvent = () => {
  const {
    event,
    isLoading,
    userHavePermissionToSignUpToEvent,
    isGroupEvent,
    isIndividualEvent,
    eventId,
    dateTimeText,
    durationText,
    reservationsCountText,
    tenantSupportsMultisport,
    usesHalls,
    usersListName,
    hiddenClassTooltip,
    studioSettings,
    selectedUserEventDetailsId,
    openReservationDialog,
    closeReservationDialog,
    paymentDialogUserEventId,
    setPaymentDialogUserEventId,
    onInstructorClick,
    onEventArrivalConfirmationClick,
    onEventLinkGenerationIniitalized,
    navigate,
    backToCalendar,
    isMusicDialogOpen,
    openMusicDialog,
    closeMusicDialog,
    isQuickArrivalEnabled,
    onQuickArrivalClick,
  } = usePlannerEvent();
  // Mounted only around its use (it loads the suggestions), but long enough to animate out.
  const keepMusicDialog = useStaysMountedToClose(isMusicDialogOpen);

  const { mutateAsync } = useApiMutation();
  const { warnClientsWithoutEmail } = useMeetingLinkRecipientsWarning();
  const { removeParticipant } = useRemoveEventParticipant(event?.id);

  if (!event && !isLoading) {
    return (
      <SpecificPageNotFound
        text={"За съжаление няма такова събитие."}
        buttonText={"Обратно към Календара"}
        onButtonClick={backToCalendar}
      />
    );
  }

  const usersListActionButton =
    !!event && !event.isDeleted && userHavePermissionToSignUpToEvent ? (
      <div className={styles.actionButtons}>
        <NotesButton
          entityId={event?.id}
          label={"Бележки към класа"}
          getEndpoint={endpointsMetadata.planner.getEventNotes}
          createEndpoint={endpointsMetadata.planner.addEventNote}
          updateEndpoint={endpointsMetadata.planner.updateEventNote}
          deleteEndpoint={endpointsMetadata.planner.deleteEventNote}
          noResultsLabel={"Към момента няма бележки за този клас."}
        />
        <HistoryButton
          entityId={event?.id ?? 0}
          entityCreatedOn={event?.createdOn ?? new Date().toISOString()}
          timezone={studioSettings?.timezone}
          label="История на събитието"
          config={EVENT_HISTORY_CONFIG}
        />
        <RenderWhen
          condition={
            !!isIndividualEvent &&
            !!event &&
            !!event.users?.length &&
            !event.users[0]?.isCancelled
          }
        >
          <WorkoutPlanButton
            eventId={event?.id ?? 0}
            mode="admin"
            registeredUserName={event?.users?.[0]?.name}
            eventDateLabel={dateTimeText}
            eventTimeLabel={undefined}
          />
        </RenderWhen>
        <IconButton
          iconType={IconType.Duplicate}
          iconHeight={"30px"}
          iconWidth={"30px"}
          tooltip="Създаване на дубликат"
          onClick={() =>
            navigate(constructPlannerEventDuplicationHref(Number(eventId)))
          }
        />
        <RenderWhen condition={!!isGroupEvent}>
          <IconButton
            iconType={IconType.Copy}
            iconHeight={"30px"}
            iconWidth={"30px"}
            tooltip="Генериране на линк към класа"
            onClick={onEventLinkGenerationIniitalized}
          />
          <IconButton
            iconType={IconType.PlusIcon}
            iconHeight={"30px"}
            iconWidth={"30px"}
            tooltip="Добавяне на резервация"
            onClick={() =>
              navigate(constructPlannerEventSignUpHref(Number(eventId)))
            }
          />
        </RenderWhen>
      </div>
    ) : undefined;

  const nameComponent = (
    <span className={styles.root_title}>
      {event?.name}
      {((!event?.showInUserPortal && event?.type === EventType.Group) ||
        event?.type === EventType.Individual) && (
        <AppTooltip
          className={styles.root_title_icon_btn}
          content={hiddenClassTooltip}
          iconType={IconType.CrossedOutEye}
        />
      )}

      {event?.isRecurring && (
        <AppTooltip
          className={styles.root_title_icon_btn}
          content={"Този клас се повтаря."}
          iconType={IconType.RecurringIcon}
        />
      )}
    </span>
  );

  const priceText = event?.price ? <PriceDisplay minPrice={event.price} /> : "";

  return (
    <div
      className={classNames(styles.root, {
        [styles.root_autoHeightList]: !isGroupEvent,
      })}
    >
      <DefaultSection>
        <SkeletonItem value={nameComponent} isLoading={isLoading} />
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        <SectionInfoRow
          label={"Дата"}
          data={dateTimeText}
          isLoading={isLoading}
          skekletonWidth={240}
        />

        <SectionInfoRow
          label={"Продължителност"}
          data={durationText}
          isLoading={isLoading}
          skekletonWidth={140}
        />

        <SectionInfoRow
          label={"Инструктор"}
          data={
            <p
              className={styles.root_info_section_link}
              onClick={onInstructorClick}
            >
              {event?.instructor}
            </p>
          }
          isLoading={isLoading}
          skekletonWidth={400}
        />

        <SectionInfoRow
          label={"Тип на събитието"}
          data={EventTypeShortLabels[event?.type ?? EventType.Individual]}
          isLoading={isLoading}
          skekletonWidth={160}
        />

        <RenderWhen condition={!!usesHalls}>
          <SectionInfoRow
            label={"Зала"}
            data={event?.hallName ?? "няма избрана"}
            isLoading={isLoading}
            skekletonWidth={140}
          />
        </RenderWhen>

        <RenderWhen condition={!!event?.isRemote}>
          <PlannerEventMeeting meetingUrl={event?.meetingUrl} />
        </RenderWhen>

        <RenderWhen condition={!!isGroupEvent}>
          <SectionInfoRow
            label={"Записани потребители"}
            data={
              <div
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <span>{reservationsCountText}</span>
                <AppTooltip
                  content={`Броят не включва потребители със статус: ${EXCLUDED_RESERVATION_STATUSES.map(
                    (status) => UserEventStatusLabels[status],
                  ).join(", ")}`}
                  iconType={IconType.Information}
                  iconClassName={styles.infoIcon}
                />
              </div>
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <RenderWhen condition={event?.allowMusicSuggestions === true}>
          <SectionInfoRow
            label={
              <>
                Предложена музика{" "}
                <MusicSuggestionsCountBadge
                  count={event?.musicSuggestionsCount ?? 0}
                />
                :
              </>
            }
            data={
              <p
                className={styles.root_info_section_link}
                onClick={openMusicDialog}
              >
                виж &gt;
              </p>
            }
            isLoading={isLoading}
          />
        </RenderWhen>
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        <RenderWhen condition={tenantSupportsMultisport}>
          <SectionInfoRow
            label={"Разрешен ли е мултиспорт?"}
            data={
              <MultisportTag
                isAccepted={!!event?.isMultisportAvailable}
                showStatus
              />
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <SectionInfoRow
          label={"Цена"}
          data={priceText}
          isLoading={isLoading}
          skekletonWidth={140}
        />

        <RenderWhen condition={!!isGroupEvent}>
          <SectionInfoRow
            label={"Генериран оборот"}
            data={
              event?.expectedRevenue ? (
                <PriceDisplay minPrice={event.expectedRevenue} />
              ) : (
                <PriceDisplay minPrice={0.0} />
              )
            }
            isLoading={isLoading}
            skekletonWidth={90}
          />
        </RenderWhen>
      </DefaultSection>

      <ItemsList
        isLoading={isLoading}
        className={styles.list}
        headerClassName={styles.usersListHeader}
        titleClassName={styles.usersListTitle}
        searchClassName={styles.usersListSearch}
        title={usersListName}
        actionButton={usersListActionButton}
        items={event?.users ?? []}
        numberOfItems={5}
        itemTemplate={(item, index, actionButtons) => {
          return (
            <PlannerEventUserItem
              isPassed={event?.isPassed ?? false}
              user={item}
              key={item?.id ?? index}
              isLoading={isLoading}
              actionButtons={actionButtons}
              setSelectedUserEventDetailsId={openReservationDialog}
            />
          );
        }}
        swipeLeftActions={[
          {
            type: "onClick",
            permissionRoute: endpointsMetadata.planner.deregisterFromEvent,
            validator: (item: IPlannerEventUserModel) => !item.isCancelled,
            disabled: (item: IPlannerEventUserModel) =>
              item.status === UserEventStatus.Arrived ||
              item.status === UserEventStatus.OnlyPaid,
            iconType: IconType.DeleteIcon,
            // Asks (with an email option only for a client who has one), then removes them.
            onClick: removeParticipant,
          },
          {
            endpoint: endpointsMetadata.planner.reRegisterForEvent,
            enablePrompt: true,
            type: "other",
            method: "put",
            promptMessage:
              "Сигурни ли сте, че искате да запишете потребителя отново?",
            validator: (item: IPlannerEventUserModel) => item.isCancelled,
            additionalParams: () => `eventId=${event?.id}`,
            iconType: IconType.PutBackIcon,
            invalidateKeys: [[endpointsMetadata.planner.getEvent]],
            // The client of a remote event gets the Meet link again; warn when they have no email.
            onSuccess: (_data: unknown, item: IPlannerEventUserModel) => {
              if (event?.isRemote) {
                warnClientsWithoutEmail(event.id, [item.id]);
              }
            },
          },
        ]}
        swipeRightActions={[
          // Confirmed + already paid → one-click "mark as arrived" via the toggle
          // endpoint with ?intent=arrive. BE flips status to Arrived without
          // re-running the canonical arrival helper (no plan double-decrement).
          {
            type: "redirect",
            validator: (item: IPlannerEventUserModel) =>
              item.status === UserEventStatus.Confirmed &&
              !item.isCancelled &&
              item.paymentType !== undefined &&
              !!item.pricePaidInEur &&
              item.pricePaidInEur > 0,
            iconType: IconType.Valid,
            redirectOnClick: async (item: IPlannerEventUserModel) => {
              await mutateAsync({
                endpoint: `${endpointsMetadata.planner.toggleConfirmReservation}?userEventId=${item.userEventId}&intent=arrive`,
                method: "put",
                globalIsLoading: true,
                invalidateKeys: [[endpointsMetadata.planner.getEvent]],
              });
            },
          },
          // Confirmed (not yet paid) or PenaltyCancelled → navigate to the
          // arrival page so the admin can enter PaymentType + Price first.
          {
            type: "redirect",
            validator: isAwaitingArrivalPayment,
            iconType: IconType.Valid,
            redirectOnClick: (item: IPlannerEventUserModel) => {
              onEventArrivalConfirmationClick(item);
            },
          },
          // Confirmed, unpaid rows in one tap (studio setting): the client's plan, or
          // the studio's first payment type at the class price; a toast can undo it.
          {
            type: "redirect",
            validator: (item: IPlannerEventUserModel) =>
              isQuickArrivalEnabled && canQuickArrive(item),
            iconType: IconType.Bolt,
            redirectOnClick: (item: IPlannerEventUserModel) => {
              onQuickArrivalClick(item);
            },
          },
          {
            type: "redirect",
            validator: (item: IPlannerEventUserModel) =>
              (item.status === UserEventStatus.Pending ||
                item.status === UserEventStatus.WaitingList) &&
              !item.isCancelled,
            iconType: IconType.Valid,
            redirectOnClick: async (item: IPlannerEventUserModel) => {
              // First, call toggle API to confirm the reservation
              await mutateAsync({
                endpoint: `${endpointsMetadata.planner.toggleConfirmReservation}?userEventId=${item.userEventId}`,
                method: "put",
                globalIsLoading: true,
                invalidateKeys: [[endpointsMetadata.planner.getEvent]],
              });

              // Confirming emails a remote event's client the Meet link; warn when they have no email.
              if (event?.isRemote) {
                warnClientsWithoutEmail(event.id, [item.id]);
              }

              // If event requires prepayment, open payment dialog after confirmation
              if (event?.requirePrepayment) {
                setPaymentDialogUserEventId(item.userEventId);
              }
            },
          },
          {
            method: "put",
            endpoint: endpointsMetadata.planner.markUserAsNotArrived,
            enablePrompt: true,
            type: "other",
            promptMessage: UNDO_ARRIVAL_PROMPT,
            additionalParams: (item: IPlannerEventUserModel) =>
              `userEventId=${item?.userEventId}`,
            validator: (item: IPlannerEventUserModel) =>
              (item.status === UserEventStatus.Arrived ||
                item.status === UserEventStatus.OnlyPaid) &&
              !item.isCancelled,
            iconType: IconType.Invalid,
            invalidateKeys: [[endpointsMetadata.planner.getEvent]],
          },
        ]}
        enableSearch={isGroupEvent}
        fullHeight={isGroupEvent}
        // On phones this list grows with the page instead of scrolling itself
        // (PlannerEvent.module.scss), so batch loading would stop at 20 users.
        renderAllOnMobile
      />

      <AppDialog
        visible={!!selectedUserEventDetailsId && selectedUserEventDetailsId > 0}
        onHide={closeReservationDialog}
        header={"Детайли за резервацията"}
        style={{ maxWidth: "400px", width: "100%" }}
        headerClassName={styles.dialog_header}
        contentClassName={styles.dialog_content}
        children={
          <PlannerEventReservationDetails
            userEventId={selectedUserEventDetailsId}
            users={event?.users}
            isEventPassed={event?.isPassed}
            onHide={closeReservationDialog}
            studioSettings={studioSettings}
            event={event}
          />
        }
      />

      <AppDialog
        visible={paymentDialogUserEventId > 0}
        onHide={() => setPaymentDialogUserEventId(0)}
        header={"Добавяне на начин на плащане"}
      >
        <PlannerEventReservationDetailsPaymentMethodDialog
          userEventId={paymentDialogUserEventId}
          onSuccess={() => setPaymentDialogUserEventId(0)}
          showFormCompletionCheckbox={event?.requireFormCompletion}
        />
      </AppDialog>

      {event && keepMusicDialog && (
        <MusicSuggestionsDialog
          eventId={event.id}
          isOpen={isMusicDialogOpen}
          onClose={closeMusicDialog}
          showSearch={false}
          isAdminView={true}
        />
      )}
    </div>
  );
};
